import Consts from '@/api/Consts';
import type { GnssBadge } from '@/api/Consts';

import type { MapBaseStationStored, SubNetworkRaw } from '@/api/ApiTypes';
import type { MetricDbItem, MetricDbItems } from '@/stores/types/mapStoreContracts';
import type { VrsAccuracyStatusKey } from '@/utils/types/baseStationsTypes';

// Structural subset of the map feature data consumed here; status_live/mntpnt_int
// originate from MapBaseStationStored (see ApiTypes) via createFeatureData.
type VrsAccuracyFeatureData = Pick<MapBaseStationStored, 'status_live' | 'mntpnt_int'>;

type ActualGnssItem = Extract<typeof Consts.GNSS_DATA[number], { readonly isActual: true }>;

export type SubNetworkSummary = {
	id: number;
	title: string;
	total: number;
	inSolution: number;
	gnssBadges: GnssBadge[];
	coverageCalculatedAt: string | null;
	residualsUpdatedAt: string | null;
};

const VRS_ACCURACY_GNSS_CODES = Consts.GNSS_DATA
	.filter((item): item is ActualGnssItem => item.isActual)
	.map(item => item.code);

const VRS_ACCURACY_SOLUTION_STATE = {
	noTraffic: 'no_traffic',
	hasTraffic: 'has_traffic',
	trafficNoSolution: 'traffic_no_solution',
	inSolution: 'in_solution',
} as const satisfies Record<string, VrsAccuracyStatusKey>;

const hasTraffic = (featureData: VrsAccuracyFeatureData): boolean => featureData.status_live !== 'no_live_data';

const hasGnssPresence = (gnssItems: readonly string[] | Set<string> | null | undefined): boolean => {
	if (gnssItems == null) {
		return false;
	}
	if (gnssItems instanceof Set) {
		return gnssItems.size > 0;
	}
	return gnssItems.length > 0;
};

const hasGnssCode = (gnssItems: readonly string[] | Set<string> | null | undefined, code: string): boolean => {
	if (gnssItems == null) {
		return false;
	}
	if (gnssItems instanceof Set) {
		return gnssItems.has(code);
	}
	return gnssItems.includes(code);
};

const convertDbItemToGNSSPresenceList = (dbItem: MetricDbItem | null | undefined): string[] => {
	return dbItem != null ? Object.keys(dbItem) : [];
};

export const getStationSolutionState = (
	featureData: VrsAccuracyFeatureData,
	dbItems: MetricDbItems | null | undefined,
	allSubnetworksMode: boolean,
): VrsAccuracyStatusKey => {
	if (!hasTraffic(featureData)) {
		return VRS_ACCURACY_SOLUTION_STATE.noTraffic;
	}
	if (allSubnetworksMode) {
		return VRS_ACCURACY_SOLUTION_STATE.hasTraffic;
	}
	const mountpoint = featureData.mntpnt_int;
	const dbItem = (dbItems != null && mountpoint != null) ? dbItems.get(mountpoint) : null;
	const mountpointResiduals = convertDbItemToGNSSPresenceList(dbItem);
	if (hasGnssPresence(mountpointResiduals)) {
		return VRS_ACCURACY_SOLUTION_STATE.inSolution;
	}
	return VRS_ACCURACY_SOLUTION_STATE.trafficNoSolution;
};

const convertDbItemsToGNSSPresenceList = (dbItems: MetricDbItems): Map<string, string[]> => {
	const result = new Map<string, string[]>();
	for (const [mountpoint, value] of dbItems.entries()) {
		result.set(mountpoint, convertDbItemToGNSSPresenceList(value));
	}
	return result;
};

/*
# TODO: temporary solution
Это упрощенный вариант dbItems, в котором мы храним только mountpoints и gnss, без значений.
Используется скорее как временное решение
*/

export const getStationGnssBadges = (mountpointResiduals: MetricDbItem | null | undefined): GnssBadge[] => {
	const mountpointResidualsList = convertDbItemToGNSSPresenceList(mountpointResiduals);
	return VRS_ACCURACY_GNSS_CODES.map(code => ({
		code,
		forceGrey: !hasGnssCode(mountpointResidualsList, code),
	}));
};

const getSubnetworkGnssBadges = (mountpointsInSolution: Map<string, string[]>): GnssBadge[] => {
	const gnssInSolution = new Set<string>();
	for (const gnssSet of mountpointsInSolution.values()) {
		for (const gnss of gnssSet) {
			gnssInSolution.add(gnss);
		}
	}
	return VRS_ACCURACY_GNSS_CODES.map(code => ({
		code,
		forceGrey: !gnssInSolution.has(code),
	}));
};

const getCurrentDatetimeBright = (details: unknown): string | null => {
	if (details == null || typeof details !== 'object') {
		return null;
	}
	const rawValue = Reflect.get(details, 'currentDatetimeBright');
	return typeof rawValue === 'string' ? rawValue : null;
};

const getLatestResidualsDatetime = (dbItems: MetricDbItems): string | null => {
	let latest: string | null = null;
	for (const gnssMap of dbItems.values()) {
		for (const details of Object.values(gnssMap)) {
			const current = getCurrentDatetimeBright(details);
			if (current != null && (latest == null || current > latest)) {
				latest = current;
			}
		}
	}
	return latest;
};

export const getSubnetworkSummary = (
	subNetwork: SubNetworkRaw,
	baseStations: readonly MapBaseStationStored[],
	dbItems: MetricDbItems,
): SubNetworkSummary => {
	const mountpointsInSolution = convertDbItemsToGNSSPresenceList(dbItems);
	const baseStationIds = subNetwork.base_stations.map(item => item.id);
	const subnetBaseStations = baseStations.filter(item => baseStationIds.includes(item.id));
	let inSolutionCount = 0;
	for (const baseStation of subnetBaseStations) {
		const mountpoint = baseStation.mntpnt_int;
		const gnssSet = mountpoint != null ? mountpointsInSolution.get(mountpoint) : null;
		if (hasGnssPresence(gnssSet)) {
			inSolutionCount += 1;
		}
	}
	return {
		id: subNetwork.id,
		title: subNetwork.title,
		total: subnetBaseStations.length,
		inSolution: inSolutionCount,
		gnssBadges: getSubnetworkGnssBadges(mountpointsInSolution),
		coverageCalculatedAt: subNetwork.coverage_calculated_at,
		residualsUpdatedAt: getLatestResidualsDatetime(dbItems),
	};
};
